import { useForm, useFieldArray, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
  InputGroup,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { modalFormSchema, type ModalFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { PollsStatus } from '@/model/polls';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostPollsMutation } from '@/services/queries/polls';
import { useRef } from 'react';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { ImagePreview } from '@/components/ImagePreview';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { compressImageToBudget } from '@/utils/image';
import { MAX_POLL_OPTIONS } from '../../constants';

export interface PollsCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  onDone: VoidFunction;
}

export const PollsCreateModal = ({ isOpen, onClose, onDone }: PollsCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const postMutation = usePostPollsMutation();

  const resolver: Resolver<ModalFormData> = zodResolver(modalFormSchema);

  const form = useForm<ModalFormData>({
    resolver,
    defaultValues: {
      question: '',
      identifier: '',
      status: PollsStatus.PUBLISHED,
      allow_multiple_votes: false,
      show_partial_results: false,
      disable_comments: false,
      enable_home: true,
      has_closing_date: false,
      closing_date: '',
      poll_options: [
        { uuid: uuidv4(), is_editing: true, option_text: '', image: undefined },
        { uuid: uuidv4(), is_editing: true, option_text: '', image: undefined },
      ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: 'poll_options',
    control: form.control,
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
    control,
  } = form;

  const formValues = watch();

  const handlePreviewActionClick = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (currentField) {
      update(index, {
        ...currentField,
        image: undefined,
      });
    }

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handlePreviewDeleteClick = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (inputRefs?.current?.[index]) {
      const input = inputRefs.current[index] as HTMLInputElement;
      input.value = '';
      input.files = null;
    }

    if (currentField) {
      update(index, {
        ...currentField,
        image: undefined,
      });
    }
  };

  const handleSavePollQuestion = async (index: number) => {
    await form.trigger(`poll_options.${index}.option_text`);
    await form.trigger(`poll_options.${index}.image`);

    if (getPollOptionFieldInvalid(index)) return;

    const currentField = formValues?.poll_options?.[index];

    update(index, {
      ...currentField,
      is_editing: false,
    });
  };

  const handleEditPollQuestion = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (currentField) {
      update(index, {
        ...currentField,
        is_editing: true,
      });
    }
  };

  const handleDeletePollQuestion = (index: number) => {
    remove(index);
  };

  const renameFile = (file: File, newName: string): File => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handleCreate = (data: ModalFormData) => {
    const mediaFiles: Record<string, File> = {};

    data.poll_options?.forEach(option => {
      if (option.image) {
        mediaFiles[option.uuid] = option.image;
      }
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      question: data.question,
      identifier: data.identifier,
      allow_multiple_votes: data.allow_multiple_votes,
      show_partial_results: data.show_partial_results,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      status: data.status as PollsStatus,
      closing_date: data.has_closing_date ? data.closing_date : undefined,
      poll_options:
        data.poll_options?.map(option => ({
          uuid: option.uuid,
          option_text: option.option_text,
        })) ?? [],
      ...mediaFiles,
    };

    postMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          title: t('pages.application_polls.components.polls_create_modal.polls_create_create_success'),
          status: 'success',
        });

        onDone?.();
        reset();
      },

      onError: (e: any) => {
        toast({
          title: e?.message || t('pages.application_polls.components.polls_create_modal.polls_create_create_error'),
          status: 'error',
        });
      },
    });
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const getPollOptionFieldInvalid = (index: number) => {
    const errorField = form?.formState?.errors?.poll_options?.[index];

    return !!errorField?.option_text || !!errorField?.image;
  };

  const getPollOptionFieldErrorMessage = (index: number) => {
    const errorField = form?.formState?.errors?.poll_options?.[index];

    return errorField?.option_text?.message?.toString() ?? errorField?.image?.message?.toString() ?? '';
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' ref={formRef} onSubmit={handleSubmit(handleCreate)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_polls.components.polls_create_modal.polls_create_title')}
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_polls.components.polls_create_modal.polls_create_team_label')}
                placeholder={t('pages.application_polls.components.polls_create_modal.polls_create_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />

              <InputField
                withoutFloating
                label={t('pages.application_polls.components.polls_create_modal.polls_create_label')}
                helperText={t('pages.application_polls.components.polls_create_modal.polls_create_helper_text')}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                withoutFloating
                label={t('pages.application_polls.components.polls_create_modal.polls_create_label_enq')}
                helperText={t('pages.application_polls.components.polls_create_modal.polls_create_helper_text_100')}
                maxLength={100}
                {...register('question')}
                rootProps={{ isInvalid: !!errors?.question, errorMessage: errors?.question?.message }}
              />

              <InputGroup display='flex' flexDirection='column' gap={3}>
                <Text fontWeight='medium'>
                  {t('pages.application_polls.components.polls_create_modal.polls_create_title_response')}
                </Text>

                {fields.map((field, index) => (
                  <Flex key={field.id} gap={3} alignItems='center'>
                    {field.is_editing && !field.image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          ref={el => (inputRefs.current[index] = el)}
                          label={t('pages.application_polls.components.polls_create_modal.polls_create_image_label')}
                          onFileChange={async file => {
                            if (!file) return;

                            const currentField = formValues?.poll_options?.[index];

                            if (currentField) {
                              const compressedFile = await compressImageToBudget(file);
                              const extension = compressedFile.name.split('.').pop();
                              const renamedFile = renameFile(compressedFile, `${field.uuid}.${extension}`);

                              const updatedField = {
                                ...currentField,
                                image: renamedFile,
                              };

                              form.setValue(`poll_options.${index}`, updatedField);
                              update(index, updatedField);
                            }

                            form.trigger(`poll_options.${index}.image`);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={field.image ? URL.createObjectURL(field.image) : undefined}
                        actionIconName='image'
                        onActionClick={() => handlePreviewActionClick(index)}
                        onDeleteClick={() => handlePreviewDeleteClick(index)}
                        withoutHover={!field.is_editing}
                      />
                    )}

                    {field?.is_editing ? (
                      <InputField
                        withoutFloating
                        label={t('pages.application_polls.components.polls_create_modal.polls_create_option_label', {
                          index: index + 1,
                        })}
                        helperText={t('pages.application_polls.components.polls_create_modal.polls_create_helper_text_50')}
                        maxLength={50}
                        width='100%'
                        {...form.register(`poll_options.${index}.option_text`)}
                        rootProps={{
                          isInvalid: getPollOptionFieldInvalid(index),
                          errorMessage: getPollOptionFieldErrorMessage(index),
                        }}
                      />
                    ) : (
                      <Flex direction='column' gap={1} w='100%'>
                        <Text fontWeight='medium'>
                          {t('pages.application_polls.components.polls_create_modal.polls_create_option_label', {
                            index: index + 1,
                          })}
                        </Text>
                        <Text>{field.option_text}</Text>
                      </Flex>
                    )}

                    {field?.is_editing ? (
                      <Button size='xs' h='24px' onClick={() => handleSavePollQuestion(index)}>
                        {t('pages.application_polls.components.polls_create_modal.polls_create_save_option')}
                      </Button>
                    ) : (
                      <Flex gap={4}>
                        <ChakraFeatherIcon
                          name='edit-2'
                          color='brand.500'
                          w='20px'
                          cursor='pointer'
                          onClick={() => handleEditPollQuestion(index)}
                        />
                        <ChakraFeatherIcon
                          name='trash-2'
                          color='red.500'
                          w='20px'
                          cursor='pointer'
                          onClick={() => handleDeletePollQuestion(index)}
                        />
                      </Flex>
                    )}
                  </Flex>
                ))}

                {form.formState.errors?.poll_options?.root ? (
                  <Text fontSize='14px' color='red.500'>
                    {form.formState.errors?.poll_options?.root?.message}
                  </Text>
                ) : null}

                <Button
                  alignSelf='flex-start'
                  variant='link'
                  color='brand.500'
                  textDecor='underline'
                  display='flex'
                  alignItems='center'
                  gap={2}
                  isDisabled={fields.length >= MAX_POLL_OPTIONS}
                  onClick={() => {
                    form.clearErrors();
                    append({ uuid: crypto.randomUUID(), option_text: '', image: '', is_editing: true });
                  }}
                >
                  <ChakraFeatherIcon name='plus' />
                  {t('pages.application_polls.components.polls_create_modal.polls_create_add_option')}
                </Button>
              </InputGroup>

              <Switch
                isChecked={!!formValues.has_closing_date}
                onChange={event => setValue('has_closing_date', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_create_modal.polls_create_has_closing_date')}
              </Switch>

              {formValues?.has_closing_date ? (
                <InputField
                  withoutFloating
                  label={t('pages.application_polls.components.polls_create_modal.polls_create_label_date')}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(new Date())}
                  rootProps={{ isInvalid: !!errors?.closing_date, errorMessage: errors?.closing_date?.message }}
                  {...register('closing_date')}
                />
              ) : null}

              <Switch
                isChecked={!!formValues.allow_multiple_votes}
                onChange={event => setValue('allow_multiple_votes', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_create_modal.polls_create_allow_multiple_votes')}
              </Switch>

              <Switch
                isChecked={!!formValues.show_partial_results}
                onChange={event => setValue('show_partial_results', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_create_modal.polls_create_show_partial_results')}
              </Switch>

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_create_modal.polls_create_disable_comments')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_create_modal.polls_create_enable_home')}
              </Switch>              
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Flex
              w='100%'
              direction={{ base: 'column', md: 'row' }}
              justifyContent={{ md: 'space-between' }}
              alignItems={{ md: 'center' }}
              gap={2}
            >
              <Button
                display='flex'
                alignItems='center'
                gap={2}
                onClick={() => {
                  setValue('status', PollsStatus.DRAFT);
                  formRef.current?.requestSubmit();
                }}
                width={{ base: '100%' }}
                p={0}
                color='brand.500'
                backgroundColor='transparent'
                textDecoration='underline'
                _hover={{
                  backgroundColor: 'transparent',
                  color: 'brand.500',
                }}
              >
                <ChakraFeatherIcon name='save' />
                {t('pages.application_polls.components.polls_create_modal.polls_create_save_draft')}
              </Button>

              <Flex direction={{ base: 'column', md: 'row' }} w='100%' justifyContent='end' gap={4}>
                <Button variant='outlined' color='gray.600' onClick={handleClose}>
                  {t('pages.application_polls.components.polls_create_modal.polls_create_cancel')}
                </Button>
                <Button type='submit' bg='green.500' paddingX={10}>
                  {t('pages.application_polls.components.polls_create_modal.polls_create_create')}
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={postMutation.isLoading} />
    </>
  );
};
